import { Component, ElementRef, ViewChild, Injector } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Validators } from '@angular/forms';
import { BaseResourceFormComponent } from '../../../models/base-resource-form';
import { Setor } from '../../../entidade/almoxarifado/setor.model';
import { LoginContabil } from '../../../entidade/login/login-contabil';
import { EddyAutoComplete } from '../../../models/form-components';
import { Unidade } from '../../../entidade/planejamento/unidade.model';
import { FuncaoService } from '../../../util/funcao.service';
import { GlobalService } from '../../../util/global.service';
import { UnidadeService } from '../../../unidade/service/unidade.service';
import { SetorService } from '../service/setor.service';
import * as toastr from 'toastr';
import { Pessoa } from '../../../entidade/comum/pessoa.model';
import { PessoaService } from '../../../pessoa/service/pessoa.service';
import { SetorAlmoxarifado } from '../../../entidade/almoxarifado/setor-almoxarifado.model';
import { SetorLocalizacao } from '../../../entidade/almoxarifado/setor-localizacao.model';
import { SetorResponsavel } from '../../../entidade/almoxarifado/setor-responsavel.model';


@Component({
  selector: 'app-setor-form',
  templateUrl: './setor-form.component.html'
})
export class SetorFormComponent extends BaseResourceFormComponent<Setor, LoginContabil> {

  /**
   * Declaração de variáveis
   */
  public estoques: SetorAlmoxarifado[] = [];
  public localizacoes: SetorLocalizacao[] = [];
  public responsaveis: SetorResponsavel[] = [];

  @ViewChild('sigla') inputSigla: ElementRef;

  public unidadeAutoComplete: EddyAutoComplete<Unidade>;
  public pessoaAutoComplete: EddyAutoComplete<Pessoa>;

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    protected injector: Injector,
    private messageService: MessageService,
    protected funcaoService: FuncaoService,
    protected globalService: GlobalService,
    protected unidadeService: UnidadeService,
    protected setorService: SetorService,
    private pessoaService: PessoaService) {
    super(new Setor(), injector, Setor.converteJson, setorService);
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      nome: [null, [Validators.required]],
      cep: [null],
      endereco: [null],
      bairro: [null],
      telefone: [null],
      sigla: [null],
      email: [null],
      ativo: [false, [Validators.required]],
      responsavel: [null],
      cargo: [null],
      setor_protocolo: [false, [Validators.required]],
      aux: [null],
      orgao: [this.login.orgao, [Validators.required]],
      unidade: [null],
      estoques: [null],
      localizacoes: [null],
      responsaveis: [null],
      complemento: [null]
    });
  }

  protected parametrosExtras(): {} {
    return { relations: 'unidade,estoques.estoque,estoques.setor,responsavel,localizacoes,responsaveis' };
  }

  protected afterLoad() {
    this.estoques = this.entidade.estoques;
    this.localizacoes = this.entidade.localizacoes;
    this.responsaveis = this.entidade.responsaveis;
  }

  protected afterInit(): void {
    this.carregarAutoCompletes();
  }

  protected campoFoco(): ElementRef {
    return this.inputSigla;
  }

  protected beforeSubmit() {
    try {
      if (!this.entidadeForm.get('sigla').value) {
        throw new Error('Informe a sigla!');
      }
      if (!this.entidadeForm.get('nome').value) {
        throw new Error('Informe o nome!');
      }
      if (!this.entidadeForm.get('unidade').value) {
        throw new Error('Informe a unidade administrativa!');
      }
      if (!this.entidadeForm.get('responsavel').value) {
        throw new Error('Informe o responsável!');
      }
      if (!this.entidadeForm.get('cargo').value) {
        throw new Error('Informe o cargo!');
      }
      if (!this.entidadeForm.get('cep').value) {
        throw new Error('Informe o CEP!');
      }
      if (!this.entidadeForm.get('telefone').value) {
        throw new Error('Informe o telefone!');
      }
      if (!this.entidadeForm.get('email').value) {
        throw new Error('Informe o e-mail!');
      }
      if (!this.entidadeForm.get('endereco').value) {
        throw new Error('Informe o endereço!');
      }
      if (!this.entidadeForm.get('bairro').value) {
        throw new Error('Informe o bairro!');
      }
      this.entidadeForm.get('estoques').setValue(this.estoques);
      this.entidadeForm.get('responsaveis').setValue(this.responsaveis);
    } catch (e) {
      toastr.error(e.message);
      throw e;
    }
  }

  protected afterSubmit(modelo: Setor) {
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================

  private carregarAutoCompletes() {
    // autocomplete para unidade
    this.unidadeAutoComplete = new EddyAutoComplete(this.entidadeForm.get('unidade'), this.unidadeService,
      'id', ['codigo', 'nome'], {
      orderBy: 'nome', 'ppa.id': this.login.ppa.id
    }, { number: ['codigo'], text: ['nome'] }
    );

    // autocomplete para pessoa
    this.pessoaAutoComplete = new EddyAutoComplete(this.entidadeForm.get('pessoa'), this.pessoaService,
      'id', ['nome'], { cidade_id: this.login.cidade.id, orderBy: 'nome' }, { number: ['id', 'cpf_cnpj'], text: ['nome'] }
    );
  }

  public carregarResponsavel(pessoa: Pessoa) {
    this.entidadeForm.get('responsavel').setValue(pessoa);
  }

  public buscarCEP() {
    if (this.entidadeForm.get('cep').value) {
      let cep = this.entidadeForm.get('cep').value;
      this.buscaCEP(cep)
        .subscribe((endereco) => {
          if (!endereco) {
            toastr.warning('Endereço não foi encontrado')
            return;
          }
          this.entidadeForm.get('endereco').setValue(endereco.logradouro);
          this.entidadeForm.get('bairro').setValue(endereco.bairro);
        }, (err) => toastr.warning('Endereço não foi encontrado'));
    }
  }

  public atualizarResponsavel(res: SetorResponsavel) {
    this.entidadeForm.get('responsavel').setValue(res.responsavel);
  }

}
